(() => {
  const root = document.documentElement;
  const body = document.body;
  root.classList.add('motion-ready');
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const themeConfig = window.LASTheme || {};
  const storefrontRoot = String(themeConfig.routes?.root || '/').replace(/\/?$/, '/');
  const cartRoutes = {
    cart: themeConfig.routes?.cart || `${storefrontRoot}cart`,
    add: themeConfig.routes?.cartAdd || `${storefrontRoot}cart/add.js`,
    change: themeConfig.routes?.cartChange || `${storefrontRoot}cart/change.js`
  };
  const cartStrings = {
    added: themeConfig.strings?.added || 'Added to your selection',
    bundleAdded: themeConfig.strings?.bundleAdded || 'Selected bundle added to your cart',
    updated: themeConfig.strings?.updated || 'Selection updated',
    removed: themeConfig.strings?.removed || 'Piece removed',
    addError: themeConfig.strings?.addError || 'This piece could not be added.',
    updateError: themeConfig.strings?.updateError || 'Unable to update quantity.',
    removeError: themeConfig.strings?.removeError || 'Unable to remove this item.'
  };

  const updateThemeUI = () => {
    const isLight = root.dataset.theme === 'light';
    qsa('[data-theme-toggle]').forEach((button) => {
      button.setAttribute('aria-pressed', String(isLight));
      button.setAttribute('aria-label', `Switch to ${isLight ? 'dark' : 'light'} mode`);
    });
    requestAnimationFrame(() => {
      const themeColor = getComputedStyle(root).getPropertyValue('--color-bg').trim();
      qs('[data-theme-color]')?.setAttribute('content', themeColor);
    });
  };

  const applyTheme = (theme, persist = false) => {
    root.dataset.theme = theme;
    if (persist) { try { localStorage.setItem('las-theme', theme); } catch (_) {} }
    updateThemeUI();
  };

  updateThemeUI();
  let savedTheme;
  try { savedTheme = localStorage.getItem('las-theme'); } catch (_) {}
  if (root.dataset.themeMode === 'system' && !savedTheme) {
    window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', (event) => applyTheme(event.matches ? 'light' : 'dark'));
  }

  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);

  const formatMoney = (cents, format = '₹{{amount}}') => {
    const number = Number(cents || 0) / 100;
    const values = {
      amount: number.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      amount_no_decimals: Math.round(number).toLocaleString('en-IN'),
      amount_with_comma_separator: number.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      amount_no_decimals_with_comma_separator: Math.round(number).toLocaleString('de-DE')
    };
    return format.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (match, key) => values[key] ?? values.amount);
  };

  const toast = (message, tone = 'neutral') => {
    const element = qs('[data-toast]');
    if (!element) return;
    element.textContent = message;
    element.dataset.tone = tone;
    element.classList.add('is-visible');
    clearTimeout(window.LASToastTimer);
    window.LASToastTimer = setTimeout(() => {
      element.classList.remove('is-visible');
      delete element.dataset.tone;
    }, 3000);
  };

  const lockScroll = (locked) => {
    body.style.overflow = locked ? 'hidden' : '';
  };

  let activeOverlayTrigger = null;

  const closeMegaMenu = () => {
    const menu = qs('[data-mega-menu]');
    const trigger = qs('[data-mega-toggle]');
    menu?.classList.remove('is-open');
    menu?.setAttribute('aria-hidden', 'true');
    trigger?.setAttribute('aria-expanded', 'false');
    if (!qs('.drawer.is-open,.modal-overlay.is-open,.filters.is-open')) lockScroll(false);
  };

  const closeOverlays = () => {
    closeMegaMenu();
    qsa('.drawer.is-open,.modal-overlay.is-open,.filters.is-open').forEach((element) => {
      element.classList.remove('is-open');
      element.classList.remove('is-dragging');
      element.setAttribute('aria-hidden', 'true');
      element.style.removeProperty('--menu-sheet-drag');
      element.style.removeProperty('--menu-sheet-progress');
    });
    qsa('[data-filter-toggle][aria-expanded="true"]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    qsa('[aria-expanded="true"][data-drawer-open],[aria-expanded="true"][data-search-open],[aria-expanded="true"][data-catalog-open],[aria-expanded="true"][data-product-lightbox-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    lockScroll(false);
    qsa('.drawer video,.modal-overlay video').forEach((video) => video.pause());
    if (activeOverlayTrigger?.isConnected) activeOverlayTrigger.focus({ preventScroll: true });
    activeOverlayTrigger = null;
  };

  const openOverlay = (element, trigger) => {
    if (!element) return;
    closeOverlays();
    element.classList.add('is-open');
    element.setAttribute('aria-hidden', 'false');
    if (element.id) qsa(`[aria-controls="${element.id}"]`).forEach((control) => control.setAttribute('aria-expanded', 'true'));
    else trigger?.setAttribute('aria-expanded', 'true');
    activeOverlayTrigger = trigger || document.activeElement;
    lockScroll(true);
    setTimeout(() => qs('button,input,a,select,textarea', element)?.focus(), 120);
  };

  document.addEventListener('click', (event) => {
    const drawerTrigger = event.target.closest('[data-drawer-open]');
    const searchTrigger = event.target.closest('[data-search-open]');
    const closeTrigger = event.target.closest('[data-overlay-close]');
    const filterTrigger = event.target.closest('[data-filter-toggle]');
    const megaToggle = event.target.closest('[data-mega-toggle]');
    const megaTab = event.target.closest('[data-mega-tab]');
    const megaClose = event.target.closest('[data-mega-close]');
    const themeToggle = event.target.closest('[data-theme-toggle]');

    if (themeToggle) {
      event.preventDefault();
      applyTheme(root.dataset.theme === 'light' ? 'dark' : 'light', true);
    }

    if (megaToggle) {
      event.preventDefault();
      const menu = qs('[data-mega-menu]');
      const opening = !menu?.classList.contains('is-open');
      closeOverlays();
      if (opening && menu) {
        menu.classList.add('is-open');
        menu.setAttribute('aria-hidden', 'false');
        megaToggle.setAttribute('aria-expanded', 'true');
        activeOverlayTrigger = megaToggle;
        lockScroll(true);
        setTimeout(() => qs('[data-mega-tab]', menu)?.focus({ preventScroll: true }), 120);
      }
    }

    if (megaTab) {
      event.preventDefault();
      const index = megaTab.dataset.megaTab;
      qsa('[data-mega-tab]').forEach((tab) => {
        const active = tab.dataset.megaTab === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });
      qsa('[data-mega-panel]').forEach((panel) => {
        const active = panel.dataset.megaPanel === index;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    }

    if (megaClose) {
      event.preventDefault();
      closeMegaMenu();
      qs('[data-mega-toggle]')?.focus({ preventScroll: true });
      activeOverlayTrigger = null;
    }

    if (drawerTrigger) {
      event.preventDefault();
      if (drawerTrigger.dataset.drawerOpen === 'CartDrawer') openSynchronizedCart(drawerTrigger);
      else openOverlay(qs(`#${drawerTrigger.dataset.drawerOpen}`), drawerTrigger);
    }
    if (searchTrigger) {
      event.preventDefault();
      openOverlay(qs('#SearchModal'), searchTrigger);
      setTimeout(() => qs('[data-predictive-input]')?.focus(), 150);
    }
    if (closeTrigger || event.target.matches('.drawer.is-open,.modal-overlay.is-open')) closeOverlays();
    if (filterTrigger) {
      const filters = qs('[data-filters]');
      const opening = !filters?.classList.contains('is-open');
      filters?.classList.toggle('is-open', opening);
      filters?.setAttribute('aria-hidden', String(!opening));
      qsa('[data-filter-toggle]').forEach((trigger) => trigger.setAttribute('aria-expanded', String(opening)));
      if (window.matchMedia('(max-width: 1100px)').matches) lockScroll(opening);
    }
    if (qs('[data-mega-menu]')?.classList.contains('is-open') && !event.target.closest('.site-header-wrap')) closeMegaMenu();
    if (qs('[data-filters]')?.classList.contains('is-open') && !event.target.closest('[data-filters]') && !filterTrigger) {
      qs('[data-filters]')?.classList.remove('is-open');
      qs('[data-filters]')?.setAttribute('aria-hidden', 'true');
      qsa('[data-filter-toggle]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      lockScroll(false);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeOverlays();
    if (event.key !== 'Tab') return;
    const overlay = qs('.drawer.is-open,.modal-overlay.is-open,.mega-navigation.is-open');
    if (!overlay) return;
    const focusable = qsa('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', overlay).filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });







let lastScrollY = Math.max(window.scrollY, 0);

const onScroll = () => {
  const currentScrollY = Math.max(window.scrollY, 0);

  root.classList.toggle('is-scrolled', currentScrollY > 36);

  const overlayOpen = Boolean(
    qs('[data-mega-menu]')?.classList.contains('is-open') ||
    qs('.drawer.is-open,.modal-overlay.is-open,.filters.is-open')
  );

  if (currentScrollY <= 80 || overlayOpen) {
    root.classList.remove('header-hidden');
  } else if (currentScrollY > lastScrollY) {
    root.classList.add('header-hidden');
  } else if (currentScrollY < lastScrollY) {
    root.classList.remove('header-hidden');
  }

  lastScrollY = currentScrollY;
};

onScroll();
window.addEventListener('scroll', onScroll, { passive: true });








  if (!reducedMotion && root.dataset.motion === 'true') {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    qsa('.reveal').forEach((element, index) => {
      element.style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
      revealObserver.observe(element);
    });
  } else {
    qsa('.reveal').forEach((element) => element.classList.add('is-visible'));
  }

  const cursorLight = qs('[data-cursor-light]');
  if (cursorLight && window.matchMedia('(pointer:fine)').matches) {
    window.addEventListener('pointermove', (event) => {
      cursorLight.style.left = `${event.clientX}px`;
      cursorLight.style.top = `${event.clientY}px`;
    }, { passive: true });
  }

  /*
   * Product navigation is warmed conservatively so the destination document can
   * be available before a shopper taps. Data Saver and slower connections are
   * always respected, and the small global budget prevents catalogue crawling.
   */
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const productPrefetchEnabled = root.dataset.prefetchProducts !== 'false'
    && !window.Shopify?.designMode
    && !connection?.saveData
    && !['slow-2g', '2g'].includes(connection?.effectiveType);
  const prefetchedProductPages = new Set();
  const PRODUCT_PREFETCH_LIMIT = 8;

  const productURLFromLink = (link) => {
    if (!link?.href) return null;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return null;
    url.hash = '';
    return url;
  };

  const prefetchProductLink = (link) => {
    if (!productPrefetchEnabled || document.hidden || prefetchedProductPages.size >= PRODUCT_PREFETCH_LIMIT) return false;
    const url = productURLFromLink(link);
    if (!url || prefetchedProductPages.has(url.href)) return false;
    const hint = document.createElement('link');
    hint.rel = 'prefetch';
    hint.setAttribute('as', 'document');
    hint.href = url.href;
    hint.dataset.noirPrefetch = 'product';
    document.head.appendChild(hint);
    prefetchedProductPages.add(url.href);
    return true;
  };

  if (productPrefetchEnabled) {
    document.addEventListener('pointerover', (event) => prefetchProductLink(event.target.closest('a[href]')), { passive: true });
    document.addEventListener('focusin', (event) => prefetchProductLink(event.target.closest('a[href]')));
    document.addEventListener('touchstart', (event) => prefetchProductLink(event.target.closest('a[href]')), { passive: true });

    const warmVisibleProducts = () => {
      if (!('IntersectionObserver' in window) || document.hidden) return;
      let remaining = 4;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || remaining <= 0) return;
          observer.unobserve(entry.target);
          if (prefetchProductLink(entry.target)) remaining -= 1;
          if (remaining <= 0) observer.disconnect();
        });
      }, { rootMargin: '180px 0px', threshold: 0.01 });
      qsa('a[href*="/products/"]').slice(0, PRODUCT_PREFETCH_LIMIT).forEach((link) => observer.observe(link));
    };

    const scheduleProductWarmup = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(warmVisibleProducts, { timeout: 1400 });
      else setTimeout(warmVisibleProducts, 700);
    };
    if (document.readyState === 'complete') scheduleProductWarmup();
    else window.addEventListener('load', scheduleProductWarmup, { once: true });
  }

  if (body.classList.contains('has-page-transitions') && !reducedMotion && !window.Shopify?.designMode) {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = new URL(link.href, window.location.href);
      if (link.target || url.origin !== window.location.origin || url.hash || link.hasAttribute('download') || link.hasAttribute('data-no-transition')) return;
      event.preventDefault();
      closeOverlays();
      const productSurface = link.closest('[data-product-card], .hot-product');
      if (productSurface && productURLFromLink(link)) productSurface.classList.add('is-navigating');
      else root.classList.add('is-leaving');
      /* Start the request on the next paint. The previous 380 ms timer delayed
         every request and made an already-fast Shopify response feel slow. */
      requestAnimationFrame(() => { window.location.href = url.href; });
    });
    window.addEventListener('pageshow', () => {
      root.classList.remove('is-leaving');
      qsa('.is-navigating').forEach((element) => element.classList.remove('is-navigating'));
    });
  }

  const responseError = async (response, fallback) => {
    const error = await response.json().catch(() => ({}));
    return new Error(error.description || error.message || fallback);
  };

  const updateCartCount = (count, animate = false) => {
    const normalized = String(Math.max(0, Number(count) || 0));
    qsa('[data-cart-count]').forEach((element) => {
      element.textContent = normalized;
      element.hidden = normalized === '0';
      if (!animate) return;
      element.classList.remove('is-bumped');
      void element.offsetWidth;
      element.classList.add('is-bumped');
      setTimeout(() => element.classList.remove('is-bumped'), 650);
    });
  };

  const cartFocusState = (drawer) => {
    const active = document.activeElement;
    const line = active?.closest?.('[data-cart-key]');
    if (!drawer?.contains(active) || !line) return null;
    let control = 'quantity';
    if (active.matches('[data-cart-remove]')) control = 'remove';
    if (active.matches('[data-quantity-button="minus"]')) control = 'minus';
    if (active.matches('[data-quantity-button="plus"]')) control = 'plus';
    return { key: line.dataset.cartKey, control };
  };

  const restoreCartFocus = (drawer, focusState) => {
    if (!drawer || !focusState) return;
    const line = qsa('[data-cart-key]', drawer).find((item) => item.dataset.cartKey === focusState.key);
    if (!line) {
      qs('[data-overlay-close],a[href],button', drawer)?.focus({ preventScroll: true });
      return;
    }
    const selectors = {
      quantity: '[data-cart-quantity]',
      remove: '[data-cart-remove]',
      minus: '[data-quantity-button="minus"]',
      plus: '[data-quantity-button="plus"]'
    };
    qs(selectors[focusState.control] || selectors.quantity, line)?.focus({ preventScroll: true });
  };

  let cartRefreshSequence = 0;
  let lastCartSyncAt = 0;

  const refreshCart = async (open = true, options = {}) => {
    const requestId = ++cartRefreshSequence;
    const current = qs('[data-cart-drawer]');
    if (!current) throw new Error(cartStrings.updateError);
    const preserveOpen = current?.classList.contains('is-open') || false;
    const focusState = cartFocusState(current);
    const drawerScrollTop = qs('.drawer__content', current)?.scrollTop || 0;
    const preserveNote = options.preserveNote ?? preserveOpen;
    const pendingNote = preserveNote ? qs('[name="note"]', current)?.value : undefined;
    const sectionURL = current?.dataset.cartSectionUrl || `${storefrontRoot}?sections=cart-drawer`;
    const requestURL = new URL(sectionURL, window.location.origin);
    requestURL.searchParams.set('_cart_sync', `${Date.now()}-${requestId}`);
    const response = await fetch(requestURL.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw await responseError(response, cartStrings.updateError);
    const sections = await response.json();
    const html = sections['cart-drawer'];
    if (!html) throw new Error(cartStrings.updateError);
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = qs('[data-cart-drawer]', parsed);
    if (!fresh) throw new Error(cartStrings.updateError);

    /* A slow response must never replace a cart refreshed by a newer request. */
    if (requestId !== cartRefreshSequence) return qs('[data-cart-drawer]');

    const liveCurrent = qs('[data-cart-drawer]');
    if (!liveCurrent) throw new Error(cartStrings.updateError);
    const freshNote = qs('[name="note"]', fresh);
    if (freshNote && pendingNote !== undefined) freshNote.value = pendingNote;
    liveCurrent.replaceWith(fresh);
    lastCartSyncAt = Date.now();
    updateCartCount(fresh.dataset.cartItemCount || 0, options.animateCount ?? open);

    if (open) {
      openOverlay(fresh, options.trigger || qs('[data-drawer-open="CartDrawer"]'));
    } else if (preserveOpen) {
      fresh.classList.add('is-open');
      fresh.setAttribute('aria-hidden', 'false');
      qsa('[data-drawer-open="CartDrawer"]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
      lockScroll(true);
      requestAnimationFrame(() => {
        const freshContent = qs('.drawer__content', fresh);
        if (freshContent) freshContent.scrollTop = drawerScrollTop;
        restoreCartFocus(fresh, focusState);
      });
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: Number(fresh.dataset.cartItemCount || 0) } }));
    return fresh;
  };

  const announceCartStatus = (drawer, message) => {
    const status = qs('[data-cart-status]', drawer);
    if (!status) return;
    status.textContent = message;
    status.classList.remove('is-visible');
    void status.offsetWidth;
    status.classList.add('is-visible');
    setTimeout(() => {
      if (!status.isConnected) return;
      status.classList.remove('is-visible');
    }, 1800);
  };

  let cartMutationQueue = Promise.resolve();
  const queueCartMutation = (operation) => {
    const queued = cartMutationQueue.catch(() => {}).then(operation);
    cartMutationQueue = queued;
    return queued;
  };

  const setCartTriggersBusy = (busy) => {
    qsa('[data-drawer-open="CartDrawer"]').forEach((trigger) => {
      trigger.classList.toggle('is-cart-syncing', busy);
      if (busy) trigger.setAttribute('aria-busy', 'true');
      else trigger.removeAttribute('aria-busy');
    });
  };

  const openSynchronizedCart = (trigger) => {
    if (trigger?.getAttribute('aria-busy') === 'true') return;
    const fallbackDrawer = qs('[data-cart-drawer]');
    setCartTriggersBusy(true);
    queueCartMutation(() => refreshCart(true, { trigger, animateCount: false, preserveNote: false }))
      .catch((error) => {
        if (fallbackDrawer?.isConnected) openOverlay(fallbackDrawer, trigger);
        toast(error.message || cartStrings.updateError, 'error');
      })
      .finally(() => setCartTriggersBusy(false));
  };

  const silentlySyncCart = () => {
    const preserveNote = qs('[data-cart-drawer]')?.classList.contains('is-open') || false;
    return queueCartMutation(() => refreshCart(false, { animateCount: false, preserveNote })).catch(() => {});
  };

  const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
  window.addEventListener('pageshow', (event) => {
    root.classList.remove('is-leaving');
    if (event.persisted || navigationEntry?.type === 'back_forward') silentlySyncCart();
  });
  window.addEventListener('pagehide', closeOverlays);

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && Date.now() - lastCartSyncAt > 10000) silentlySyncCart();
  });

  const addVariant = async (variantId, quantity = 1) => {
    const response = await fetch(cartRoutes.add, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(quantity) }] })
    });
    if (!response.ok) throw await responseError(response, cartStrings.addError);
    await refreshCart(true);
  };

  const changeCartItem = async (key, line, quantity) => {
    const payload = key ? { id: key, quantity } : { line: Number(line), quantity };
    const response = await fetch(cartRoutes.change, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw await responseError(response, quantity === 0 ? cartStrings.removeError : cartStrings.updateError);
    return response.json();
  };

  const setCartLineBusy = (line, busy, removing = false) => {
    if (!line) return;
    line.classList.toggle('is-updating', busy && !removing);
    line.classList.toggle('is-removing', busy && removing);
    line.setAttribute('aria-busy', String(busy));
    qsa('button,input', line).forEach((control) => { control.disabled = busy; });
    line.closest('[data-cart-drawer]')?.querySelector('.drawer__panel')?.setAttribute('aria-busy', String(busy));
  };

  const addFeedbackTimers = new WeakMap();
  const showAddSuccess = (button, text = 'Added ✓') => {
    if (!button) return;
    clearTimeout(addFeedbackTimers.get(button));
    const label = qs('span', button);
    const originalText = label?.textContent;
    const originalAria = button.getAttribute('aria-label');
    button.classList.remove('is-loading');
    button.classList.add('is-added');
    if (label) label.textContent = text;
    button.setAttribute('aria-label', `${button.dataset.productTitle || originalText || 'Item'} added to cart`);
    addFeedbackTimers.set(button, setTimeout(() => {
      button.classList.remove('is-added');
      if (label && originalText) label.textContent = originalText;
      if (originalAria) button.setAttribute('aria-label', originalAria);
    }, 1200));
  };

  document.addEventListener('click', async (event) => {
    const quickAdd = event.target.closest('[data-quick-add]');
    if (!quickAdd) return;
    event.preventDefault();
    if (quickAdd.disabled) return;
    quickAdd.disabled = true;
    quickAdd.classList.remove('is-added');
    quickAdd.classList.add('is-loading');
    quickAdd.setAttribute('aria-busy', 'true');
    try {
      await queueCartMutation(() => addVariant(quickAdd.dataset.quickAdd, Number(quickAdd.dataset.quickAddQuantity || 1)));
      showAddSuccess(quickAdd);
      toast(cartStrings.added, 'success');
    } catch (error) {
      quickAdd.classList.remove('is-loading');
      toast(error.message || cartStrings.addError, 'error');
    } finally {
      quickAdd.disabled = false;
      quickAdd.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || form.dataset.ajax === 'false') return;
    event.preventDefault();
    const button = qs('[type="submit"]', form);
    const errorElement = qs('[data-product-form-error]', form);
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
    button?.setAttribute('aria-busy', 'true');
    button?.classList.add('is-loading');
    if (button) button.disabled = true;
    try {
      await queueCartMutation(async () => {
        const response = await fetch(cartRoutes.add, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        if (!response.ok) throw await responseError(response, cartStrings.addError);
        await refreshCart(true);
      });
      showAddSuccess(button);
      toast(cartStrings.added, 'success');
    } catch (error) {
      button?.classList.remove('is-loading');
      if (errorElement) {
        errorElement.textContent = error.message || cartStrings.addError;
        errorElement.hidden = false;
      }
      toast(error.message || cartStrings.addError, 'error');
    } finally {
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  });

  const normalizedCartQuantity = (input) => {
    const requested = Number.parseInt(input.value, 10);
    if (!Number.isFinite(requested)) return null;
    if (requested <= 0) return 0;
    const minimum = Math.max(1, Number(input.dataset.ruleMin || 1));
    const step = Math.max(1, Number(input.step || 1));
    const maximum = input.max ? Number(input.max) : Infinity;
    return Math.min(maximum, minimum + Math.max(0, Math.round((requested - minimum) / step)) * step);
  };

  document.addEventListener('change', async (event) => {
    const quantityInput = event.target.closest('[data-cart-quantity]');
    if (!quantityInput) return;
    const quantity = normalizedCartQuantity(quantityInput);
    const previousQuantity = Number(quantityInput.dataset.currentQuantity || 0);
    if (quantity === null || quantity === previousQuantity) {
      quantityInput.value = previousQuantity;
      return;
    }
    quantityInput.value = quantity;
    const line = quantityInput.closest('[data-cart-line]');
    setCartLineBusy(line, true, quantity === 0);
    try {
      await queueCartMutation(async () => {
        await changeCartItem(quantityInput.dataset.lineKey, quantityInput.dataset.line, quantity);
        const fresh = await refreshCart(false);
        announceCartStatus(fresh, quantity === 0 ? cartStrings.removed : cartStrings.updated);
      });
    } catch (error) {
      if (quantityInput.isConnected) quantityInput.value = previousQuantity;
      toast(error.message || cartStrings.updateError, 'error');
    } finally {
      if (line?.isConnected) setCartLineBusy(line, false);
      qs('[data-cart-drawer] .drawer__panel')?.setAttribute('aria-busy', 'false');
    }
  });

  document.addEventListener('click', async (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    if (remove.disabled) return;
    const line = remove.closest('[data-cart-line]');
    setCartLineBusy(line, true, true);
    try {
      await queueCartMutation(async () => {
        await changeCartItem(remove.dataset.lineKey, remove.dataset.line, 0);
        const fresh = await refreshCart(false);
        announceCartStatus(fresh, cartStrings.removed);
      });
    } catch (error) {
      toast(error.message || cartStrings.removeError, 'error');
    } finally {
      if (line?.isConnected) setCartLineBusy(line, false);
      qs('[data-cart-drawer] .drawer__panel')?.setAttribute('aria-busy', 'false');
    }
  });

  let cartPageUpdateTimer;
  const scheduleCartPageUpdate = (input) => {
    const form = input?.closest('[data-cart-page-form]');
    if (!form) return;
    const updateButton = qs('[data-cart-page-update]', form);
    if (!updateButton) return;
    form.classList.add('is-updating');
    updateButton.setAttribute('aria-busy', 'true');
    updateButton.classList.add('is-loading');
    clearTimeout(cartPageUpdateTimer);
    cartPageUpdateTimer = setTimeout(() => {
      if (typeof form.requestSubmit === 'function') form.requestSubmit(updateButton);
      else updateButton.click();
    }, 320);
  };

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-page-quantity]');
    if (!input) return;
    const quantity = normalizedCartQuantity(input);
    if (quantity === null) return;
    input.value = quantity;
    scheduleCartPageUpdate(input);
  });

  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-quantity-button]');
    if (!quantityButton) return;
    if (quantityButton.disabled) return;
    const wrapper = quantityButton.closest('.quantity');
    const input = qs('input[type="number"]', wrapper);
    if (!input) return;
    const step = Number(input.step || 1);
    const direction = quantityButton.dataset.quantityButton === 'plus' ? step : -step;
    const maximum = input.max ? Number(input.max) : Infinity;
    input.value = Math.min(maximum, Math.max(Number(input.min || 1), Number(input.value || 1) + direction));
    if (input.hasAttribute('data-cart-quantity')) input.dispatchEvent(new Event('change', { bubbles: true }));
    else if (input.hasAttribute('data-cart-page-quantity')) {
      const normalized = normalizedCartQuantity(input);
      if (normalized !== null) input.value = normalized;
      scheduleCartPageUpdate(input);
    }
  });

  document.addEventListener('click', (event) => {
    const bulkOption = event.target.closest('[data-bulk-quantity]');
    if (!bulkOption) return;
    const productRoot = bulkOption.closest('[data-product-root]');
    const quantityInput = qs('[data-product-form] [name="quantity"]', productRoot);
    if (!quantityInput) return;
    const minimum = Number(quantityInput.min || 1);
    const step = Number(quantityInput.step || 1);
    const requested = Number(bulkOption.dataset.bulkQuantity);
    quantityInput.value = minimum + Math.max(0, Math.ceil((requested - minimum) / step)) * step;
    qsa('[data-bulk-quantity]', productRoot).forEach((option) => option.classList.toggle('is-active', option === bulkOption));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  });

  const updateBundleTotal = (form) => {
    const total = qsa('[data-bundle-item]', form).reduce((sum, item) => {
      const check = qs('[data-bundle-check]', item);
      if (!check?.checked) return sum;
      const variant = qs('[data-bundle-variant]', item);
      const selected = variant?.tagName === 'SELECT' ? variant.selectedOptions[0] : variant;
      return sum + Number(selected?.dataset.price || 0);
    }, 0);
    const output = qs('[data-bundle-total]', form);
    if (output) output.textContent = formatMoney(total, form.dataset.moneyFormat || '₹{{amount}}');
  };

  qsa('[data-bundle-form]').forEach(updateBundleTotal);
  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-bundle-form]');
    if (form) updateBundleTotal(form);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-bundle-form]');
    if (!form) return;
    event.preventDefault();
    const items = qsa('[data-bundle-item]', form).flatMap((item) => {
      const check = qs('[data-bundle-check]', item);
      const variant = qs('[data-bundle-variant]', item);
      return check?.checked && variant?.value ? [{ id: Number(variant.value), quantity: 1 }] : [];
    });
    const button = qs('[type="submit"]', form);
    if (!items.length) return;
    button?.setAttribute('aria-busy', 'true');
    button?.classList.add('is-loading');
    if (button) button.disabled = true;
    try {
      await queueCartMutation(async () => {
        const response = await fetch(cartRoutes.add, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw await responseError(response, 'This bundle could not be added.');
        await refreshCart(true);
      });
      showAddSuccess(button, 'Bundle added ✓');
      toast(cartStrings.bundleAdded, 'success');
    } catch (error) {
      button?.classList.remove('is-loading');
      toast(error.message, 'error');
    } finally {
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  });

  document.addEventListener('click', (event) => {
    const pill = event.target.closest('[data-option-value]');
    if (!pill) return;
    event.preventDefault();
    const select = document.getElementById(pill.dataset.optionTarget);
    if (!select) return;
    select.value = pill.dataset.optionValue;
    const group = pill.closest('.variant-pills');
    if (!group) return;
    qsa('[data-option-value]', group).forEach((item) => {
      const active = item === pill;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });
    select.dispatchEvent(new Event('change', { bubbles: true }));
  });

  qsa('[data-product-root]').forEach((productRoot) => {
    const variantData = qs('[data-variant-json]', productRoot);
    if (!variantData) return;
    const variants = JSON.parse(variantData.textContent);
    const selectors = qsa('[data-option-position]', productRoot);
    const idInput = qs('[name="id"]', productRoot);
    const addButton = qs('[data-product-submit]', productRoot);
    const priceElement = qs('[data-product-price]', productRoot);
    const compareElement = qs('[data-product-compare]', productRoot);
    const savingElement = qs('[data-product-saving]', productRoot);
    const unitPriceElement = qs('[data-unit-price]', productRoot);
    const stickyPrice = qs('[data-sticky-buy] small', productRoot);
    const skuElement = qs('[data-product-sku]', productRoot);
    const stockElement = qs('[data-product-stock]', productRoot);
    const stockItem = stockElement?.closest('.product-identity__inventory');
    const variantMetaElement = qs('[data-variant-meta]', productRoot);
    let variantMeta = {};
    try { variantMeta = variantMetaElement ? JSON.parse(variantMetaElement.textContent) : {}; } catch (_) {}
    const moneyFormat = productRoot.dataset.moneyFormat || '₹{{amount}}';

    const updateVariant = () => {
      const options = selectors.map((select) => select.value);
      const variant = variants.find((item) => item.options.every((option, index) => option === options[index]));
      if (!variant) {
        if (addButton) {
          addButton.disabled = true;
          qs('span', addButton).textContent = 'Unavailable';
        }
        return;
      }
      if (idInput) idInput.value = variant.id;
      const quantityInput = qs('[name="quantity"]', productRoot);
      if (quantityInput && variant.quantity_rule) {
        quantityInput.min = variant.quantity_rule.min || 1;
        quantityInput.step = variant.quantity_rule.increment || 1;
        if (variant.quantity_rule.max) quantityInput.max = variant.quantity_rule.max;
        else quantityInput.removeAttribute('max');
        if (Number(quantityInput.value) < Number(quantityInput.min)) quantityInput.value = quantityInput.min;
      }
      if (priceElement) priceElement.textContent = formatMoney(variant.price, moneyFormat);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, moneyFormat);
      if (compareElement) {
        compareElement.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price, moneyFormat) : '';
        compareElement.hidden = !(variant.compare_at_price > variant.price);
      }
      if (savingElement) {
        const onSale = variant.compare_at_price > variant.price;
        savingElement.textContent = onSale ? `Save ${Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)}%` : '';
        savingElement.hidden = !onSale;
      }
      if (unitPriceElement) {
        const measurement = variant.unit_price_measurement;
        const reference = measurement ? `${measurement.reference_value === 1 ? '' : measurement.reference_value}${measurement.reference_unit}` : '';
        unitPriceElement.textContent = measurement && variant.unit_price ? `${formatMoney(variant.unit_price, moneyFormat)} / ${reference}` : '';
        unitPriceElement.hidden = !(measurement && variant.unit_price);
      }
      const meta = variantMeta[String(variant.id)] || {};
      if (skuElement) skuElement.textContent = meta.sku || variant.sku || 'Not assigned';
      if (stockElement) {
        const quantity = Number(meta.inventory_quantity || 0);
        const tracked = Boolean(meta.inventory_management);
        let label = variant.available ? 'In stock' : 'Sold out';
        let tone = variant.available ? 'available' : 'sold-out';
        if (tracked && quantity > 0) label = `${quantity} unit${quantity === 1 ? '' : 's'} left`;
        else if (tracked && meta.inventory_policy === 'continue') { label = 'Available to order'; tone = 'continue'; }
        else if (tracked && !variant.available) { label = 'Sold out'; tone = 'sold-out'; }
        const dot = stockElement.querySelector('i')?.outerHTML || '<i aria-hidden="true"></i>';
        stockElement.innerHTML = `${dot}${escapeHTML(label)}`;
        if (stockItem) stockItem.dataset.inventoryTone = tone;
      }
      if (addButton) {
        addButton.disabled = !variant.available;
        qs('span', addButton).textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
      selectors.forEach((select) => {
        const position = select.dataset.optionPosition;
        const optionLabel = qs(`[data-option-label="${position}"]`, productRoot);
        if (optionLabel) optionLabel.textContent = select.value;
        qsa('[data-option-target]', productRoot).filter((pill) => pill.dataset.optionTarget === select.id).forEach((pill) => {
          const active = pill.dataset.optionValue === select.value;
          pill.classList.toggle('is-active', active);
          pill.setAttribute('aria-pressed', String(active));
        });
      });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      productRoot.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    };
    selectors.forEach((selector) => selector.addEventListener('change', updateVariant));
  });

  const setProductLightboxImage = (lightbox, requestedIndex) => {
    const thumbs = qsa('[data-product-lightbox-thumb]', lightbox);
    if (!thumbs.length) return;
    const index = (Number(requestedIndex) + thumbs.length) % thumbs.length;
    const thumb = thumbs[index];
    const mainImage = qs('[data-product-lightbox-main]', lightbox);
    if (mainImage) {
      mainImage.src = thumb.dataset.lightboxSrc;
      mainImage.alt = thumb.dataset.lightboxAlt || '';
      const intrinsicWidth = Number(thumb.dataset.lightboxWidth);
      const intrinsicHeight = Number(thumb.dataset.lightboxHeight);
      if (intrinsicWidth > 0 && intrinsicHeight > 0) {
        mainImage.width = intrinsicWidth;
        mainImage.height = intrinsicHeight;
        mainImage.style.aspectRatio = `${intrinsicWidth} / ${intrinsicHeight}`;
      } else {
        mainImage.style.removeProperty('aspect-ratio');
      }
    }
    thumbs.forEach((item, itemIndex) => {
      item.classList.toggle('is-active', itemIndex === index);
      item.setAttribute('aria-current', itemIndex === index ? 'true' : 'false');
    });
    lightbox.dataset.activeIndex = String(index);
    const count = qs('[data-product-lightbox-count]', lightbox);
    if (count) count.textContent = `${index + 1} / ${thumbs.length}`;
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-product-lightbox-open]');
    const thumbnail = event.target.closest('[data-product-lightbox-thumb]');
    const direction = event.target.closest('[data-lightbox-direction]');
    if (opener) {
      event.preventDefault();
      const lightbox = document.getElementById(opener.getAttribute('aria-controls'));
      if (!lightbox) return;
      setProductLightboxImage(lightbox, opener.dataset.mediaIndex || 0);
      openOverlay(lightbox, opener);
      return;
    }
    if (thumbnail) {
      event.preventDefault();
      const lightbox = thumbnail.closest('[data-product-lightbox]');
      if (lightbox) setProductLightboxImage(lightbox, thumbnail.dataset.mediaIndex || 0);
      return;
    }
    if (direction) {
      event.preventDefault();
      const lightbox = direction.closest('[data-product-lightbox]');
      if (!lightbox) return;
      const delta = direction.dataset.lightboxDirection === 'next' ? 1 : -1;
      setProductLightboxImage(lightbox, Number(lightbox.dataset.activeIndex || 0) + delta);
    }
  });

  document.addEventListener('keydown', (event) => {
    const lightbox = qs('[data-product-lightbox].is-open');
    if (!lightbox || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    setProductLightboxImage(lightbox, Number(lightbox.dataset.activeIndex || 0) + (event.key === 'ArrowRight' ? 1 : -1));
  });

  const initProductGallerySwipe = (scope = document) => {
    qsa('.product-gallery', scope).forEach((gallery) => {
      if (gallery.dataset.swipeReady === 'true') return;
      gallery.dataset.swipeReady = 'true';
      let pointerId = null;
      let startX = 0;
      let startY = 0;
      let horizontalGesture = false;
      let clickTimer = 0;
      let scrollTimer = 0;

      const updateActiveMedia = () => {
        const slides = qsa('.product-media', gallery);
        if (!slides.length) return;
        const galleryCenter = gallery.scrollLeft + (gallery.clientWidth / 2);
        const activeIndex = slides.reduce((closest, slide, index) => {
          const center = slide.offsetLeft + (slide.offsetWidth / 2);
          const closestSlide = slides[closest];
          const closestCenter = closestSlide.offsetLeft + (closestSlide.offsetWidth / 2);
          return Math.abs(center - galleryCenter) < Math.abs(closestCenter - galleryCenter) ? index : closest;
        }, 0);
        gallery.dataset.activeMedia = String(activeIndex);
      };

      gallery.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' || event.target.closest('video,model-viewer')) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        horizontalGesture = false;
      }, { passive: true });

      gallery.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        const deltaX = Math.abs(event.clientX - startX);
        const deltaY = Math.abs(event.clientY - startY);
        if (deltaX > 8 && deltaX > deltaY) horizontalGesture = true;
      }, { passive: true });

      const release = (event) => {
        if (event.pointerId !== pointerId) return;
        pointerId = null;
        if (horizontalGesture) {
          gallery.dataset.suppressClick = 'true';
          window.clearTimeout(clickTimer);
          clickTimer = window.setTimeout(() => delete gallery.dataset.suppressClick, 220);
        }
        horizontalGesture = false;
      };
      gallery.addEventListener('pointerup', release, { passive: true });
      gallery.addEventListener('pointercancel', release, { passive: true });
      gallery.addEventListener('scroll', () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(updateActiveMedia, 90);
      }, { passive: true });
      gallery.addEventListener('click', (event) => {
        if (gallery.dataset.suppressClick !== 'true') return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
    });
  };

  document.addEventListener('click', (event) => {
    const hotspot = event.target.closest('[data-look-hotspot]');
    const lookClose = event.target.closest('[data-look-close]');
    if (!hotspot && !lookClose) return;
    const stage = event.target.closest('[data-look-stage]');
    if (!stage) return;
    if (lookClose) {
      qsa('[data-look-panel]', stage).forEach((panel) => { panel.classList.remove('is-open'); panel.setAttribute('aria-hidden', 'true'); });
      qsa('[data-look-hotspot]', stage).forEach((button) => button.setAttribute('aria-expanded', 'false'));
      return;
    }
    const id = hotspot.dataset.lookHotspot;
    qsa('[data-look-panel]', stage).forEach((panel) => {
      const active = panel.dataset.lookPanel === id && !panel.classList.contains('is-open');
      panel.classList.toggle('is-open', active);
      panel.setAttribute('aria-hidden', String(!active));
    });
    qsa('[data-look-hotspot]', stage).forEach((button) => button.setAttribute('aria-expanded', String(button === hotspot && qs(`[data-look-panel="${id}"]`, stage)?.classList.contains('is-open'))));
  });

  document.addEventListener('input', (event) => {
    const range = event.target.closest('[data-compare-range]');
    if (range) range.closest('[data-before-after]')?.style.setProperty('--compare-position', `${range.value}%`);
  });

  document.addEventListener('click', async (event) => {
    const toggle = event.target.closest('[data-reel-toggle]');
    if (!toggle) return;
    const card = toggle.closest('.reel-card');
    const video = qs('video', card);
    if (!video) return;
    if (video.paused) {
      qsa('.reel-card video').forEach((other) => { if (other !== video) { other.pause(); other.closest('.reel-card')?.classList.remove('is-playing'); } });
      video.muted = false;
      video.volume = 1;
      try { await video.play(); card.classList.add('is-playing'); toggle.setAttribute('aria-label', 'Pause video'); } catch (_) {}
    } else {
      video.pause();
      card.classList.remove('is-playing');
      toggle.setAttribute('aria-label', 'Play video');
    }
  });

  const predictiveInput = qs('[data-predictive-input]');
  const predictiveResults = qs('[data-predictive-results]');
  let searchTimer;
  predictiveInput?.addEventListener('input', () => {
    clearTimeout(searchTimer);
    const query = predictiveInput.value.trim();
    if (query.length < 2) {
      predictiveResults.innerHTML = '';
      return;
    }
    searchTimer = setTimeout(async () => {
      predictiveResults.setAttribute('aria-busy', 'true');
      try {
        const fields = 'title,product_type,variants.title,variants.sku,vendor';
        const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][fields]=${encodeURIComponent(fields)}&resources[options][unavailable_products]=last`);
        const data = await response.json();
        const products = data.resources?.results?.products || [];
        predictiveResults.innerHTML = products.length ? products.map((product) => {
          const matchingVariant = product.variants?.find((variant) => String(variant.sku || '').toLowerCase().includes(query.toLowerCase()));
          const detail = matchingVariant?.sku ? `SKU ${matchingVariant.sku}` : product.price;
          return `
          <a class="predictive-card" href="${escapeHTML(matchingVariant?.url || product.url)}">
            ${product.image ? `<img src="${escapeHTML(product.image)}" alt="" width="144" height="160" loading="lazy">` : ''}
            <span><strong>${escapeHTML(product.title)}</strong><br>${escapeHTML(detail)}</span>
          </a>`;
        }).join('') : `<p>${escapeHTML('No pieces or SKUs found')}</p>`;
      } catch (_) {
        predictiveResults.innerHTML = '';
      } finally {
        predictiveResults.removeAttribute('aria-busy');
      }
    }, 220);
  });

  const productForm = qs('[data-product-form]');
  const stickyBuy = qs('[data-sticky-buy]');
  if (productForm && stickyBuy && window.IntersectionObserver) {
    const stickyObserver = new IntersectionObserver(([entry]) => stickyBuy.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0.25 });
    stickyObserver.observe(productForm);
    qs('[data-sticky-submit]', stickyBuy)?.addEventListener('click', () => qs('[type="submit"]', productForm)?.click());
  }

  const initProductRecommendations = (scope = document) => {
    qsa('product-recommendations[data-url]', scope).forEach(async (element) => {
      if (element.innerHTML.trim() || element.dataset.recommendationsLoading === 'true') return;
      element.dataset.recommendationsLoading = 'true';
      try {
        const response = await fetch(element.dataset.url);
        if (!response.ok) return;
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = qs('product-recommendations', parsed);
        if (fresh?.innerHTML.trim()) {
          element.innerHTML = fresh.innerHTML;
          qsa('.reveal', element).forEach((item) => item.classList.add('is-visible'));
          qsa('[data-bundle-form]', element).forEach(updateBundleTotal);
        }
      } catch (_) {
      } finally {
        element.dataset.recommendationsLoading = 'false';
      }
    });
  };
  initProductRecommendations();

  const initMegaNavigation = (scope = document) => {
    const menu = qs('[data-mega-menu]', scope) || (scope.matches?.('[data-mega-menu]') ? scope : null);
    if (!menu || menu.dataset.enhanced === 'true') return;
    menu.dataset.enhanced = 'true';
    const activateTab = (tab) => {
      if (!tab) return;
      const index = tab.dataset.megaTab;
      qsa('[data-mega-tab]', menu).forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      qsa('[data-mega-panel]', menu).forEach((panel) => {
        const active = panel.dataset.megaPanel === index;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    };
    qsa('[data-mega-tab]', menu).forEach((tab) => {
      tab.addEventListener('mouseenter', () => {
        if (window.matchMedia('(hover:hover) and (min-width:1101px)').matches) activateTab(tab);
      });
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const tabs = qsa('[data-mega-tab]', menu);
        const current = tabs.indexOf(tab);
        const next = event.key === 'Home' ? tabs[0] : event.key === 'End' ? tabs.at(-1) : tabs[(current + (event.key === 'ArrowDown' ? 1 : -1) + tabs.length) % tabs.length];
        activateTab(next);
        next.focus();
      });
    });
  };

  const initScrollExpand = (scope = document) => {
    qsa('[data-scroll-expand]', scope).forEach((section) => {
      if (section.dataset.expandReady === 'true') return;
      section.dataset.expandReady = 'true';
      const frame = qs('[data-scroll-expand-frame]', section);
      const intro = qs('.scroll-expand__intro', section);
      const styles = getComputedStyle(section);
      const startWidth = parseFloat(styles.getPropertyValue('--expand-start')) || 38;
      const endHeight = parseFloat(styles.getPropertyValue('--expand-height')) || 82;
      let queued = false;
      const update = () => {
        queued = false;
        const rect = section.getBoundingClientRect();
        const distance = Math.max(section.offsetHeight - window.innerHeight, 1);
        const progress = Math.max(0, Math.min(1, -rect.top / distance));
        frame?.style.setProperty('--expand-progress', progress.toFixed(4));
        frame?.style.setProperty('--expand-width', `${startWidth + ((100 - startWidth) * progress)}%`);
        frame?.style.setProperty('--expand-current-height', `${28 + ((endHeight - 28) * progress)}vh`);
        frame?.style.setProperty('--expand-offset', `${(window.innerWidth <= 820 ? 15 : 11) * (1 - progress)}vh`);
        if (intro) {
          intro.style.opacity = String(Math.max(0, 1 - (progress * 2.15)));
          intro.style.transform = `translateY(${-progress * 24}px)`;
        }
      };
      const requestUpdate = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(update);
      };
      update();
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
    });
  };

  const initDragScroll = (scope = document) => {
    qsa('[data-drag-scroll]', scope).forEach((rail) => {
      if (rail.dataset.dragReady === 'true') return;
      rail.dataset.dragReady = 'true';
      let startX = 0;
      let startScroll = 0;
      let moved = false;
      rail.addEventListener('pointerdown', (event) => {
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        moved = false;
        rail.classList.add('is-dragging');
        rail.setPointerCapture?.(event.pointerId);
      });
      rail.addEventListener('pointermove', (event) => {
        if (!rail.classList.contains('is-dragging')) return;
        const delta = event.clientX - startX;
        moved = moved || Math.abs(delta) > 6;
        rail.scrollLeft = startScroll - delta;
      });
      const finish = () => rail.classList.remove('is-dragging');
      rail.addEventListener('pointerup', finish);
      rail.addEventListener('pointercancel', finish);
      rail.addEventListener('click', (event) => { if (moved) event.preventDefault(); }, true);
    });
  };

  const initAutomationCatalog = (scope = document) => {
    qsa('[data-automation-catalog]', scope).forEach((catalog) => {
      if (catalog.dataset.catalogReady === 'true') return;
      catalog.dataset.catalogReady = 'true';
      const cards = qsa('[data-catalog-card]', catalog);
      const count = qs('[data-catalog-count]', catalog);
      qsa('[data-catalog-filter]', catalog).forEach((button) => button.addEventListener('click', () => {
        const filter = button.dataset.catalogFilter;
        let visible = 0;
        qsa('[data-catalog-filter]', catalog).forEach((item) => item.classList.toggle('is-active', item === button));
        cards.forEach((card) => {
          const show = filter === 'all' || card.dataset.category === filter;
          card.classList.toggle('is-filtered', !show);
          if (show) visible += 1;
        });
        if (count) count.textContent = `${visible} ${visible === 1 ? 'system' : 'systems'} shown`;
      }));
    });
  };

  document.addEventListener('click', (event) => {
    const catalogOpen = event.target.closest('[data-catalog-open]');
    if (catalogOpen) {
      event.preventDefault();
      const card = catalogOpen.closest('[data-catalog-card]');
      const page = catalogOpen.closest('[data-automation-page]') || catalogOpen.closest('[data-automation-catalog]')?.parentElement;
      const drawer = page ? qs('[data-automation-product-drawer]', page) : document.getElementById(catalogOpen.getAttribute('aria-controls') || '');
      const content = drawer ? qs('[data-catalog-drawer-content]', drawer) : null;
      const template = card ? qs('[data-catalog-template]', card) : null;
      if (drawer && content && template) {
        content.replaceChildren(template.content.cloneNode(true));
        openOverlay(drawer, catalogOpen);
      }
    }
  });

  document.addEventListener('click', (event) => {
    const request = event.target.closest('[data-catalog-request]');
    if (!request) return;
    if (request.closest('[data-automation-product-drawer]')) closeOverlays();
    const input = qs('[data-automation-selected]');
    if (input) {
      input.value = request.dataset.catalogRequest;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      setTimeout(() => input.focus({ preventScroll: true }), 500);
    }
  });

  document.addEventListener('pointerdown', (event) => {
    const target = event.target.closest('.icon-button,.header-action,.mobile-dock a,.mobile-dock button,.look-hotspot');
    if (!target) return;
    target.classList.remove('is-tapped');
    requestAnimationFrame(() => target.classList.add('is-tapped'));
    setTimeout(() => target.classList.remove('is-tapped'), 420);
  }, { passive: true });

  const initAnnouncementRotators = (scope = document) => {
    qsa('[data-announcement-rotator]', scope).forEach((rotator) => {
      if (rotator.dataset.announcementReady === 'true') return;
      rotator.dataset.announcementReady = 'true';
      const states = qsa('[data-announcement-state]', rotator);
      if (states.length < 2) return;
      const current = qs('[data-announcement-current]', rotator);
      const interval = Math.max(3000, Number(rotator.dataset.announcementInterval) || 5000);
      const autoplay = rotator.dataset.announcementAutoplay === 'true' && !reducedMotion;
      let index = Math.max(0, states.findIndex((state) => state.classList.contains('is-active')));
      let timer;
      const show = (nextIndex) => {
        index = (nextIndex + states.length) % states.length;
        states.forEach((state, stateIndex) => {
          const active = stateIndex === index;
          state.classList.toggle('is-active', active);
          state.setAttribute('aria-hidden', String(!active));
          if (state.matches('a')) state.tabIndex = active ? 0 : -1;
        });
        if (current) current.textContent = String(index + 1).padStart(2, '0');
      };
      const stop = () => { window.clearInterval(timer); timer = undefined; };
      const start = () => {
        stop();
        if (!autoplay) return;
        timer = window.setInterval(() => {
          if (!rotator.isConnected) { stop(); return; }
          if (!document.hidden) show(index + 1);
        }, interval);
      };
      rotator.addEventListener('mouseenter', stop);
      rotator.addEventListener('mouseleave', start);
      rotator.addEventListener('focusin', stop);
      rotator.addEventListener('focusout', (event) => { if (!rotator.contains(event.relatedTarget)) start(); });
      show(index);
      start();
    });
  };

  const initMobileMenuSheet = (scope = document) => {
    const drawer = scope.matches?.('[data-mobile-menu-sheet]') ? scope : qs('[data-mobile-menu-sheet]', scope);
    if (!drawer || drawer.dataset.mobileSheetReady === 'true') return;
    const handle = qs('[data-menu-sheet-handle]', drawer);
    if (!handle) return;
    drawer.dataset.mobileSheetReady = 'true';
    let pointerId = null;
    let startY = 0;
    let currentDistance = 0;
    let startedAt = 0;

    const resetDrag = () => {
      pointerId = null;
      currentDistance = 0;
      drawer.classList.remove('is-dragging');
      drawer.style.removeProperty('--menu-sheet-drag');
      drawer.style.removeProperty('--menu-sheet-progress');
    };

    handle.addEventListener('pointerdown', (event) => {
      if (!drawer.classList.contains('is-open') || !window.matchMedia('(max-width: 820px)').matches) return;
      pointerId = event.pointerId;
      startY = event.clientY;
      startedAt = performance.now();
      currentDistance = 0;
      drawer.classList.add('is-dragging');
      handle.setPointerCapture?.(pointerId);
      event.preventDefault();
    });

    handle.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      currentDistance = Math.max(0, event.clientY - startY);
      drawer.style.setProperty('--menu-sheet-drag', `${currentDistance}px`);
      drawer.style.setProperty('--menu-sheet-progress', String(Math.min(1, currentDistance / 220)));
      event.preventDefault();
    });

    const finishDrag = (event, cancelled = false) => {
      if (event.pointerId !== pointerId) return;
      const elapsed = Math.max(performance.now() - startedAt, 1);
      const velocity = currentDistance / elapsed;
      const shouldClose = !cancelled && (currentDistance > 96 || (currentDistance > 48 && velocity > 0.55));
      resetDrag();
      if (shouldClose) closeOverlays();
    };

    handle.addEventListener('pointerup', (event) => finishDrag(event));
    handle.addEventListener('pointercancel', (event) => finishDrag(event, true));
  };

  const initV12 = (scope = document) => {
    initMegaNavigation(scope);
    initScrollExpand(scope);
    initDragScroll(scope);
    initAutomationCatalog(scope);
    initProductRecommendations(scope);
    initAnnouncementRotators(scope);
    initProductGallerySwipe(scope);
    initMobileMenuSheet(scope);
  };
  initV12();

  document.addEventListener('shopify:section:load', (event) => {
    qsa('.reveal', event.target).forEach((element) => element.classList.add('is-visible'));
    qsa('[data-bundle-form]', event.target).forEach(updateBundleTotal);
    updateThemeUI();
    initV12(event.target);
  });
})();
